import {Form, Input, InputNumber, Spin} from 'antd';
import styles from './overview.module.scss';
import trash from 'images/trash-can-outline.svg';
import plusIcon from 'images/plus-circle.png';
import UploadImage from 'components/Startup/profile/companyImage';
import {isDev, NavTabsEnum} from 'utils/constants';
import {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {handleMutualFunds} from 'redux/store/assetClass';
import v2 from '../v2/instrumentV2.module.scss';
import {FormCard} from '../v2/FormCard';
import {useRemainingFields} from 'pages/AssetManagement/v2/useRemainingFields';
import {InstrumentActions} from '../v2/InstrumentActions';
import {FUND_DETAILS_MOCK, SAMPLE_IMAGE_URL} from '../v2/devPrefill';
import {useNavigate, useParams} from 'react-router-dom';
import {createMutualFundsStepTwoRoute} from '../Bonds/routes';
import {GET_MUTUAL_FUND_BY_LANGUAGE} from 'graphql/queries/assetManagement';
import {useMutation, useQuery} from '@apollo/client';
import {toast} from 'react-toastify';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import {handleUpdateMutualFund} from '../actions';
import {UPDATE_MUTUAL_FUND} from 'graphql/mutations/assetManagement';
import {makeSelectTabOperations} from 'redux/store/auth';

export const MutualFundDetails = () => {
  const [managerUrl, setManagerUrl] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [form] = Form.useForm();
  const {assetId, id, lang} = useParams();

  // The action bar defaults `remaining` to 0, so a screen that never
  // passes it reports "All required fields on this screen are complete"
  // on a blank form. These are the names carrying `required: true` below.
  const REQUIRED_FIELDS = ['name'];
  const [remaining, countRemaining] = useRemainingFields(
    form,
    REQUIRED_FIELDS,
    id,
  );
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [updatedValues, setUpdatedValues] = useState({});

  const fieldValues = useSelector(state => state?.assetClass?.mutualFunds);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const pageOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.assetClasses),
  );

  const {
    data,
    refetch,
    loading: loadingInstrument,
  } = useQuery(GET_MUTUAL_FUND_BY_LANGUAGE, {
    variables: {
      getFinancialInstrumentId: id,

      language: lang,
    },
    skip: !id,
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
  });

  // Populate only the fields this screen owns. Spreading the whole record into
  // the form store put every queried field — __typename, nested objects, the
  // computed ones — into onFinish, which then travelled into the mutation.
  const applyValues = source => {
    if (!source) return;
    const {managedBy} = source;
    form.setFieldsValue({
      investmentStrategy: source.investmentStrategy,
      bestSuitedFor: source.bestSuitedFor,
      assetAllocation:
        source.assetAllocation?.map(a => ({
          assetClass: a?.assetClass,
          percent: a?.percent,
        })) ?? [],
      name: managedBy?.name,
      website: managedBy?.website ?? null,
      description: managedBy?.description ?? null,
    });
    setManagerUrl(managedBy?.logo);
    setPreviewUrl(managedBy?.logo);
  };

  useEffect(() => {
    applyValues(data?.getFinancialInstrument?.data);
  }, [data?.getFinancialInstrument?.data]);

  const [updateMutualFund, {loading: loadingUpdate}] = useMutation(
    UPDATE_MUTUAL_FUND,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  useEffect(() => {
    if (fieldValues && !id) applyValues(fieldValues);
  }, [fieldValues]);

  if (loadingInstrument) {
    return (
      <div className="query-loading">
        <Spin />
        <h1>Loading...</h1>
      </div>
    );
  }

  return (
    <div>
      <div className={v2.bodyHead}>
        <h2>Fund details</h2>
        <p>
          Who runs the fund, its strategy, and how it is split across asset
          classes.
        </p>
      </div>

      <Form
        form={form}
        onValuesChange={countRemaining}
        onFinish={values => {
          const {name, website, description} = values;
          const payload = {
            investmentStrategy: values.investmentStrategy,
            bestSuitedFor: values.bestSuitedFor,
            assetAllocation:
              values.assetAllocation?.map(a => ({
                assetClass: a?.assetClass,
                percent: Number(a?.percent),
              })) ?? [],
            managedBy: {
              role: 'FUND_MANAGER',
              name,
              website: website || null,
              description: description || null,
              ...(managerUrl && {logo: managerUrl}),
            },
          };
          if (id) {
            setOpenUpdateModal(true);
            setUpdatedValues(payload);
          } else {
            dispatch(
              handleMutualFunds(payload),
              navigate(
                createMutualFundsStepTwoRoute({
                  param: assetId,
                  name: 'investments',
                }),
              ),
            );
          }
        }}
        layout="vertical">
        <FormCard
          title="Fund manager"
          description="The firm managing this fund, shown on the instrument page">
          <div className={styles.fundManager}>
            <UploadImage
              label="Upload Manager Photo"
              isACL
              previewUrl={previewUrl}
              folder="bonds"
              setPreviewUrl={setPreviewUrl}
              setImgUrl={setManagerUrl}
              bucket={
                isDev
                  ? 'dabafinancialinstruments-nonprod'
                  : 'dabafinancialinstruments-prod'
              }
            />
            <div className={styles.managerDetails}>
              <Form.Item
                rules={[{required: true, message: 'This is a required field'}]}
                className={styles.fundWidth}
                name="name"
                label="Full Name">
                <Input />
              </Form.Item>
              <Form.Item
                className={styles.fundWidth}
                name="description"
                label="About the Manager">
                <Input.TextArea rows={3} />
              </Form.Item>
              <Form.Item
                rules={[{type: 'url', message: 'Input a valid link'}]}
                className={styles.fundWidth}
                name="website"
                label="Website">
                <Input />
              </Form.Item>
            </div>
          </div>
        </FormCard>

        <FormCard
          title="Fund details"
          description="Strategy and who the fund is built for">
          <Form.Item
            label="Investment Strategy"
            className={v2.half}
            name="investmentStrategy">
            <Input.TextArea rows={4} />
          </Form.Item>
          <Form.Item
            label="Best Suited"
            className={v2.half}
            name="bestSuitedFor">
            <Input.TextArea rows={4} />
          </Form.Item>
        </FormCard>

        <FormCard
          title="Asset allocation"
          description="How the fund is split across asset classes — percentages should total 100">
          <p className={styles.hint}>
            How the fund is split across asset classes. Percentages should total
            100.
          </p>
          <Form.List name="assetAllocation">
            {(fields, {add, remove}) => (
              <>
                {fields.map(({key, name}, index) => (
                  <div className={styles.allocationRow} key={key}>
                    <div className={styles.allocationHead}>
                      <span className={styles.allocationTitle}>
                        Asset {index + 1}
                      </span>
                      <img
                        src={trash}
                        onClick={() => remove(name)}
                        alt="Remove"
                        className={styles.allocationTrash}
                      />
                    </div>
                    <div className={styles.allocationFields}>
                      <Form.Item
                        rules={[{required: true, message: 'Required'}]}
                        name={[name, 'assetClass']}
                        label="Asset">
                        <Input placeholder="Equities" />
                      </Form.Item>
                      <Form.Item
                        rules={[{required: true, message: 'Required'}]}
                        name={[name, 'percent']}
                        label="Percent">
                        <InputNumber
                          className={styles['input-number']}
                          min={0}
                          max={100}
                        />
                      </Form.Item>
                    </div>
                  </div>
                ))}
                <div onClick={add} className={styles['add-item']}>
                  <img src={plusIcon} alt="" />
                  <span>Add Asset</span>
                </div>
              </>
            )}
          </Form.List>
        </FormCard>

        <InstrumentActions
          remaining={remaining}
          onPrepopulate={() => {
            form.setFieldsValue(FUND_DETAILS_MOCK);
            setManagerUrl(SAMPLE_IMAGE_URL);
            setPreviewUrl(SAMPLE_IMAGE_URL);
            countRemaining();
          }}
          disabled={!pageOperations?.update}
          label="Save and proceed"
        />
      </Form>
      <ConfirmModal
        openModal={openUpdateModal}
        setOpenModal={() => setOpenUpdateModal(false)}
        loading={loadingUpdate}
        confirmText={'Are you sure you want to Update this Fields'}
        title="Update Mutual Fund"
        handleOperation={() =>
          handleUpdateMutualFund(
            updateMutualFund,
            id,
            lang,
            updatedValues,
            refetch,
            setOpenUpdateModal,
            true,
          )
        }
      />
    </div>
  );
};
